import type { Locale } from "@/i18n/dictionaries";
import type { ProductDetailDto } from "./product-types";
import type { ProductListCategory, ProductListItem, ProductListStyle, ProductListViewModel } from "./product-list-data";

const hanPattern = /[\u3400-\u9fff]/;

const categoryLabels: Record<string, string> = {
  "30": "Rings",
  "31": "Necklaces",
  "33": "Earrings",
  "34": "Bracelets",
  "35": "Brooches / Pins",
  "36": "Cufflinks",
  "37": "Creative Gifts",
  "戒指": "Rings",
  "颈饰": "Necklaces",
  "耳饰": "Earrings",
  "腕饰": "Bracelets",
  "胸针/徽章": "Brooches / Pins",
  "袖扣": "Cufflinks",
  "奇趣创意": "Creative Gifts"
};

const styleLabels: Record<string, string> = {
  "动物": "Animals",
  "独立设计": "Independent Design",
  "高校系列": "Campus Collection",
  "个性概念": "Conceptual",
  "纪念日": "Anniversary",
  "建筑": "Architecture",
  "街头潮流": "Street Style",
  "科学": "Science",
  "科学魅力": "Science Charm",
  "民族经典": "Ethnic Classics",
  "魔镜系列": "Mirror Collection",
  "前卫时尚": "Avant-garde",
  "清新自然": "Fresh Nature",
  "秋天": "Autumn",
  "时尚": "Fashion",
  "四季": "Four Seasons",
  "甜美浪漫": "Sweet Romance",
  "嘻哈摇滚": "Hip-hop Rock",
  "自然": "Nature",
  "微物": "Tiny Things",
  "molecular": "Molecular",
  "science": "Science"
};

const designerLabels: Record<string, string> = {
  "微物": "Tiny Things",
  "科学魅力": "Science Charm"
};

const productTitleLabels: Record<string, string> = {
  "10002479": "Pebble Ring - 57 mm Inner Circumference",
  "10002472": "NJU Ring - 17.2 mm Diameter",
  "10002136": "Bloom Bracelet",
  "10002119": "Ding Dong Mini - Right Ear",
  "10002118": "Ding Dong Mini - Left Ear",
  "10001656": "Minimal Triangle Stud Earrings",
  "10001325": "Us",
  "10001637": "Dancing Spirit",
  "10001520": "Dancing Elephant",
  "10002076": "Dancing with Imagination",
  "10002074": "Freedom Girl",
  "10002073": "Ding Dong - Left Ear",
  "10002072": "Ding Dong - Right Ear",
  "10002026": "Leaf Pendant III",
  "10002025": "Leaf Pendant II",
  "10001977": "Budding Bracelet",
  "10001976": "Leaf Earrings",
  "10001979": "Ring Mountain",
  "10001442": "INSparkle Ring",
  "10001320": "INSparkle Design #10001320"
};

export function containsHan(value: string | undefined | null) {
  return Boolean(value && hanPattern.test(value));
}

function clean(value: string | undefined | null) {
  return (value ?? "").trim();
}

export function localizeProductCategoryLabel(category: Pick<ProductListCategory, "value" | "label" | "rawLabel"> | undefined, locale: Locale) {
  const label = clean(category?.label);
  if (locale !== "en-US") return label;
  return categoryLabels[clean(category?.value)] ||
    categoryLabels[clean(category?.rawLabel)] ||
    categoryLabels[label] ||
    (containsHan(label) ? "Category" : label);
}

export function localizeProductStyleLabel(style: Pick<ProductListStyle, "value" | "label" | "rawLabel"> | string | undefined, locale: Locale) {
  const value = typeof style === "string" ? clean(style) : clean(style?.value);
  const label = typeof style === "string" ? clean(style) : clean(style?.label);
  const rawLabel = typeof style === "string" ? clean(style) : clean(style?.rawLabel);
  if (locale !== "en-US") return label;
  return styleLabels[value] || styleLabels[rawLabel] || styleLabels[label] || (containsHan(label) ? "Style" : label);
}

export function localizeProductDesignerName(name: string | undefined, locale: Locale) {
  const label = clean(name);
  if (locale !== "en-US") return label;
  return designerLabels[label] || (containsHan(label) ? "INSparkle Studio" : label);
}

export function localizeProductTitle(product: Pick<ProductListItem, "id" | "name">, locale: Locale) {
  const title = clean(product.name);
  if (locale !== "en-US") return title;
  return productTitleLabels[product.id] || (containsHan(title) ? `INSparkle Design #${product.id}` : title);
}

export function localizeProductKeyTips(keyTips: string | undefined, locale: Locale) {
  const value = clean(keyTips);
  if (!value || locale !== "en-US") return value;
  return value.split(/[,，、]/).map((part) => localizeProductStyleLabel(part, locale)).filter(Boolean).join(", ");
}

export function localizeProductListViewModel(data: ProductListViewModel, locale: Locale): ProductListViewModel {
  if (locale !== "en-US") return data;
  return {
    ...data,
    categories: data.categories.map((category) => ({ ...category, label: localizeProductCategoryLabel(category, locale) })),
    styles: data.styles.map((style) => ({ ...style, label: localizeProductStyleLabel(style, locale) })),
    products: data.products.map((product) => ({
      ...product,
      name: localizeProductTitle(product, locale),
      designer: localizeProductDesignerName(product.designer, locale),
      keyTips: localizeProductKeyTips(product.keyTips, locale)
    }))
  };
}

export function localizeProductDetail(detail: ProductDetailDto, locale: Locale): ProductDetailDto {
  if (locale !== "en-US") return detail;
  return {
    ...detail,
    title: localizeProductTitle({ id: detail.goodsId, name: detail.title }, locale),
    designerName: localizeProductDesignerName(detail.designerName, locale),
    category: detail.category
      ? {
          ...detail.category,
          label: localizeProductCategoryLabel({ value: "", label: detail.category.label, rawLabel: detail.category.label }, locale)
        }
      : detail.category,
    content: {
      ...detail.content,
      introduction: detail.content.introduction.filter((paragraph) => !containsHan(paragraph)).map((paragraph) => paragraph.trim())
    }
  };
}
